import { OnlinePortalApplicationDocumentRequest } from '@/models';
import {
	AutoFactory,
	CollectionResponseApplicationDocumentRequest,
	DocumentName,
	OnlinePortalApplicationDocumentRequestsApi,
	ApplicationDocumentRequest
} from '@sageworks/jpi';
import { GroupedDocumentRequests } from '@/models/GroupedDocumentRequests';
import { getObjectKeys } from '@/utils/ObjectUtils';

export async function getApplicationDocumentRequestsWithPossibleDocuments(
	loanApplicationId: number,
	page?: number,
	perPage?: number
): Promise<OnlinePortalApplicationDocumentRequest[]> {
	page = page ? page : 1;
	perPage = perPage ? perPage : 1000;

	const service = AutoFactory.get(OnlinePortalApplicationDocumentRequestsApi);
	const result = await service.getPagedWithPossibleDocuments(page, perPage, loanApplicationId);

	return (result.items ?? [])
		.filter(x => x.documentRequest)
		.map(x => new OnlinePortalApplicationDocumentRequest(x.documentRequest!, x.possibleDocuments ?? []));
}
/**
 * Retrieves all applicationDocumentRequests and applicationDocumentRequests from the JPI
 * Returns an array of OnlinePortalApplicationDocumentRequest
 *
 * @param page default: 1
 * @param perPage default: 1000
 */

export async function getApplicationDocumentRequests(
	loanApplicationId: number,
	page?: number,
	perPage?: number
): Promise<OnlinePortalApplicationDocumentRequest[]> {
	page = page ? page : 1;
	perPage = perPage ? perPage : 1000;

	const onlinePortalApplicationDocumentRequestsService: OnlinePortalApplicationDocumentRequestsApi = AutoFactory.get(
		OnlinePortalApplicationDocumentRequestsApi
	);

	const applicationDocumentRequests: CollectionResponseApplicationDocumentRequest = await onlinePortalApplicationDocumentRequestsService.getPaged(
		page,
		perPage,
		loanApplicationId
	);

	let newApplicationDocumentRequests = applicationDocumentRequests.items || [];
	// Transform ApplicationDocumentRequests into Online-Portal-specific OnlinePortalApplicationDocumentRequests
	return newApplicationDocumentRequests.map(documentRequest => new OnlinePortalApplicationDocumentRequest(documentRequest));
}

export async function getApplicationDocumentRequestById(id: number): Promise<OnlinePortalApplicationDocumentRequest | null> {
	const onlinePortalApplicationDocumentRequestsService: OnlinePortalApplicationDocumentRequestsApi = AutoFactory.get(
		OnlinePortalApplicationDocumentRequestsApi
	);
	const applicationDocumentRequest: ApplicationDocumentRequest = await onlinePortalApplicationDocumentRequestsService.getById(id);
	return applicationDocumentRequest ? new OnlinePortalApplicationDocumentRequest(applicationDocumentRequest) : null;
}

export function transformApplicationDocumentRequestsForBorrowerView(
	documentNames: DocumentName[],
	newApplicationDocumentRequests: OnlinePortalApplicationDocumentRequest[]
): OnlinePortalApplicationDocumentRequest[] {
	// Don't show waived requests in the borrower view, show for lender for un-waiving
	newApplicationDocumentRequests = newApplicationDocumentRequests.filter(
		documentRequest => !documentRequest.dateWaived && !documentRequest.waivedReason && !documentRequest.waivedByLoanOfficerId
	);

	// If we are in Borrower view then we need to change the name of the document to a Customer facing name
	newApplicationDocumentRequests.forEach(documentRequest => {
		if (!documentRequest.documentNamesId) return;
		let documentName: DocumentName | undefined = documentNames.find(documentName => documentName.id === documentRequest.documentNamesId);

		if (documentName?.customerFacingName != null && documentName.customerFacingName.trim() !== '') {
			documentRequest.documentName = documentName.customerFacingName;
		}
	});
	return newApplicationDocumentRequests;
}

export function groupAndSortByCustomerId(documentRequests: OnlinePortalApplicationDocumentRequest[]): GroupedDocumentRequests {
	const map = {} as GroupedDocumentRequests;

	documentRequests.forEach((request: OnlinePortalApplicationDocumentRequest) => {
		const customerId = request.customerId;

		if (customerId) {
			if (map[customerId] == null) {
				map[customerId] = [];
			}

			map[customerId]?.push(request);
		}
	});

	for (const key of getObjectKeys(map)) {
		map[key].sort((a: OnlinePortalApplicationDocumentRequest, b: OnlinePortalApplicationDocumentRequest) => {
			let aAdjustedPosition = adjustPositionOfAdditionalDocs(a, a.position);
			let bAdjustedPosition = adjustPositionOfAdditionalDocs(b, b.position);
			const aMinusB = aAdjustedPosition - bAdjustedPosition;
			// if exactly one value is an additional request just use the basic return
			// if positive a after b negative b after a for the sort
			// a 1000 - b 150 = 850, a 150 - b 1000 = -850
			if (aMinusB > 850 || aMinusB < -850) {
				return aMinusB;
			}
			// we have two requests of the same type so we can check for the null position for both a and b for both additional and original requests
			// if we have two requests with the same position, fallback to alphabetical sort
			if (aAdjustedPosition < 0 || bAdjustedPosition < 0 || aAdjustedPosition === 999 || bAdjustedPosition === 999 || aMinusB === 0) {
				return (a.documentName || '') < (b.documentName || '') ? -1 : 1;
			}
			return aMinusB;
		});
	}

	return map;
}
// -1 is the adjusted positional value for null or undefined positional values
// if the document request was added by the user it will have 1000 added to it
// 999 is therefore the null positional value for added documents
// this is to ensure that the added documents are always sorted after the original document requests
export function adjustPositionOfAdditionalDocs(docRequest: OnlinePortalApplicationDocumentRequest, position: number | null | undefined) {
	// increasing the position of added docs ensure they are sorted after position is never > 120 in the db so 1k is plenty
	if (!docRequest.id || docRequest.id < 0 || !docRequest.dateRequested) {
		return (position ?? -1) + 1000;
	}
	// position in the db is never negative so this is a replacement of that and it keeps the values as numbers
	return position ?? -1;
}

export async function createApplicationDocumentRequest(
	requestToCreate: OnlinePortalApplicationDocumentRequest
): Promise<OnlinePortalApplicationDocumentRequest> {
	const onlinePortalApplicationDocumentRequestsService: OnlinePortalApplicationDocumentRequestsApi = AutoFactory.get(
		OnlinePortalApplicationDocumentRequestsApi
	);
	var newRequest = await onlinePortalApplicationDocumentRequestsService.create(requestToCreate.request);
	return new OnlinePortalApplicationDocumentRequest(newRequest);
}

export async function updateApplicationDocumentRequest(requestToUpdate: OnlinePortalApplicationDocumentRequest): Promise<void> {
	const onlinePortalApplicationDocumentRequestsService: OnlinePortalApplicationDocumentRequestsApi = AutoFactory.get(
		OnlinePortalApplicationDocumentRequestsApi
	);
	onlinePortalApplicationDocumentRequestsService.update(requestToUpdate.id as number, requestToUpdate.request);
}

export async function deleteApplicationDocumentRequest(requestToDelete: OnlinePortalApplicationDocumentRequest): Promise<void> {
	if (!requestToDelete.id) return;
	const onlinePortalApplicationDocumentRequestsService: OnlinePortalApplicationDocumentRequestsApi = AutoFactory.get(
		OnlinePortalApplicationDocumentRequestsApi
	);
	onlinePortalApplicationDocumentRequestsService._delete(requestToDelete.id);
}

export async function reopen(requestToReopen: OnlinePortalApplicationDocumentRequest): Promise<OnlinePortalApplicationDocumentRequest> {
	(requestToReopen.dateUploaded as any) = null;
	(requestToReopen.uploadedByUsersId as any) = null;
	(requestToReopen.uploadedByUserType as any) = null;
	(requestToReopen.uploadedByUserName as any) = null;
	(requestToReopen.dateWaived as any) = null;
	(requestToReopen.waivedReason as any) = null;
	(requestToReopen.waivedByLoanOfficerId as any) = null;
	(requestToReopen.waivedByLoanOfficerName as any) = null;
	(requestToReopen.documentId as any) = null;
	(requestToReopen.notUploadedReason as any) = null;
	await updateApplicationDocumentRequest(requestToReopen);
	return Promise.resolve(requestToReopen);
}

export function getStatusMessageForDocumentRequest(documentRequest: OnlinePortalApplicationDocumentRequest) {
	let docStatusMessage = 'Open Request';

	if (documentRequest.dateUploaded) {
		docStatusMessage = `Uploaded ${documentRequest.dateUploaded}`;
	} else if (documentRequest.dateWaived && documentRequest.waivedReason === 'Document on file') {
		docStatusMessage = `Marked on file ${documentRequest.dateWaived}`;
	} else if (documentRequest.dateWaived) {
		docStatusMessage = `Waived ${documentRequest.dateWaived} for reason "${documentRequest.waivedReason}"`;
	}

	return docStatusMessage;
}
